import React from 'react';
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCalendarDate } from '../../../utils/dateFormatUtils';

const formatPhone = (phone) => {
  if (!phone?.countryCode || !phone?.number) {
    return null;
  }
  return `${phone.countryCode} ${phone.number}`;
};

const formatGenre = (genre, t) => {
  if (genre === 1 || genre === '1' || genre === 'male') {
    return t('customersManagement.genre.male');
  }
  if (genre === 2 || genre === '2' || genre === 'female') {
    return t('customersManagement.genre.female');
  }
  return null;
};

const displayValue = (value, t) => value || t('common.notProvided');

const PreviewDetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      py: 0.75,
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const CustomerImportPreviewList = ({ rows }) => {
  const { t } = useTranslation();

  if (!rows?.length) {
    return null;
  }

  const columns = [
    { id: 'name', label: t('customersManagement.table.customer') },
    { id: 'email', label: t('customersManagement.table.email') },
    { id: 'phone', label: t('customersManagement.table.phone') },
    { id: 'genre', label: t('customersManagement.table.genre') },
    { id: 'birthDate', label: t('customersManagement.table.birthDate') },
  ];

  const cellsForRow = (row) => ({
    name: row.name || t('common.notProvided'),
    email: displayValue(row.email, t),
    phone: displayValue(formatPhone(row.phone), t),
    genre: displayValue(formatGenre(row.genre, t), t),
    birthDate: row.birthDate ? formatCalendarDate(row.birthDate) : t('common.notProvided'),
  });

  return (
    <>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
        {rows.map((row) => {
          const cells = cellsForRow(row);
          const hasErrors = row.errors?.length > 0;
          return (
            <Box
              key={row.id}
              sx={{
                border: hasErrors ? '1px solid #ef9a9a' : '1px solid #e0e0e0',
                borderRadius: 2,
                p: 2,
                backgroundColor: hasErrors ? 'rgba(211, 47, 47, 0.04)' : '#fff',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 1 }}>
                {cells.name}
              </Typography>
              <Divider sx={{ mb: 0.5 }} />
              {columns.slice(1).map((column) => (
                <PreviewDetailRow key={column.id} label={column.label} value={cells[column.id]} />
              ))}
              {hasErrors ? (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {row.errors.map((error) => t(error.key)).join(', ')}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <TableContainer
        sx={{
          display: { xs: 'none', md: 'block' },
          border: '1px solid #e0e0e0',
          borderRadius: 2,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} sx={{ fontWeight: 700 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const cells = cellsForRow(row);
              const hasErrors = row.errors?.length > 0;
              return (
                <TableRow
                  key={row.id}
                  sx={hasErrors ? { backgroundColor: 'rgba(211, 47, 47, 0.04)' } : undefined}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cells.name}
                    </Typography>
                    {hasErrors ? (
                      <Typography variant="caption" color="error">
                        {row.errors.map((error) => t(error.key)).join(', ')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{cells.email}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{cells.phone}</TableCell>
                  <TableCell>{cells.genre}</TableCell>
                  <TableCell>{cells.birthDate}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CustomerImportPreviewList;
